/**
 * `authFetch` — a `fetch` wrapper for staff/customer SPAs (T11-02, design/11 §2): the access JWT
 * lives in memory, the refresh token in an HttpOnly cookie. When a request comes back `401` the
 * wrapper asks `refresh()` for a new access token **once** (concurrent 401s share the same refresh)
 * and replays the original request with it; if the refresh fails, `onAuthFailure()` is called and
 * the original 401 response is returned so the caller's error handling still runs.
 *
 * Pass the result as the `fetch` option of `createServiceClient(...)` (openapi-fetch hands it a
 * `Request`), or call it like `fetch(input, init)`.
 */

export interface AuthFetchOptions {
  /** Underlying fetch (tests inject one); defaults to `globalThis.fetch` at call time. */
  fetch?: typeof fetch;
  /** Current access token (memory); `null` when signed out. */
  getAccessToken: () => string | null | undefined;
  /**
   * Obtain a fresh access token (e.g. `POST /v1/auth/token/refresh` with the cookie). Resolve
   * `null` (or reject) when the session is gone.
   */
  refresh: () => Promise<string | null>;
  /** Called once per failed refresh — e.g. show "session expired" / go to `/login?next=`. */
  onAuthFailure?: () => void;
  /**
   * Requests that must never trigger a refresh (the auth endpoints themselves), matched against
   * the URL path. Defaults to `/auth/` paths.
   */
  skip?: (url: URL) => boolean;
}

export type AuthFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

const defaultSkip = (url: URL): boolean => /\/auth\//.test(url.pathname);

function toUrl(request: Request): URL {
  try {
    return new URL(request.url);
  } catch {
    return new URL(request.url, "http://relative.invalid");
  }
}

export function createAuthFetch(options: AuthFetchOptions): AuthFetch {
  const skip = options.skip ?? defaultSkip;
  let inflight: Promise<string | null> | null = null;

  const refreshOnce = (): Promise<string | null> => {
    if (!inflight) {
      inflight = options
        .refresh()
        .catch(() => null)
        .then((fresh) => {
          if (!fresh) {
            options.onAuthFailure?.();
          }
          return fresh;
        })
        .finally(() => {
          // let the next expiry start a new refresh (after every waiter got this result)
          queueMicrotask(() => {
            inflight = null;
          });
        });
    }
    return inflight;
  };

  return async (input, init) => {
    const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
    const original =
      input instanceof Request && init === undefined ? input : new Request(input, init);
    // keep a replayable copy before the body stream is consumed
    const replay = original.clone();
    const token = options.getAccessToken();
    if (token && !original.headers.has("Authorization")) {
      original.headers.set("Authorization", `Bearer ${token}`);
    }
    const response = await doFetch(original);
    if (response.status !== 401 || skip(toUrl(original))) {
      return response;
    }
    const fresh = await refreshOnce();
    if (!fresh) {
      return response;
    }
    replay.headers.set("Authorization", `Bearer ${fresh}`);
    return doFetch(replay);
  };
}
